import { formatDateString } from "@/v2-common/utils/utils.date"
import type { BadgeProps } from "@/v2-backoffice-client/components/badge"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { getDaysUntil } from "@/v2-backoffice-client/license/license.utils"
import { FENERUM_INVOICE_BASE_URL } from "@/v2-backoffice-client/constants"
import {
  INVOICE_FOLLOWUP_OUTCOME_PAID,
  INVOICE_FOLLOWUP_STATUS_CONTACTED,
  INVOICE_FOLLOWUP_STATUS_ISSUE,
  FINANCE_COLLECTIONS_EMAIL,
} from "@/v2-backoffice-client/license/license.constants"
import type { InvoiceCollectionsQuery } from "@/v2-backoffice-client/license/collections/__types__/license.collections.query"

export type CollectionInvoice =
  InvoiceCollectionsQuery["getInvoiceCollections"][number]

// The four conversation states, shared by the Collections board and the
// customer panel's Collections card so a row never reads differently in the
// two places.
export const COLLECTION_STATE_NOT_STARTED = "Not started"
export const COLLECTION_STATE_CONTACTED = "Contacted"
export const COLLECTION_STATE_ISSUE = "Issue – sent to Finance"
export const COLLECTION_STATE_PAID = "Paid"

export type CollectionFollowUpState =
  | typeof COLLECTION_STATE_NOT_STARTED
  | typeof COLLECTION_STATE_CONTACTED
  | typeof COLLECTION_STATE_ISSUE
  | typeof COLLECTION_STATE_PAID

// One display state per row: the derived Paid outcome wins (the mirror saw
// the money), otherwise the stored conversation state, with no row yet
// reading as Not started.
export function getCollectionFollowUpState(
  invoice: CollectionInvoice,
): CollectionFollowUpState {
  if (invoice.OutcomeCode === INVOICE_FOLLOWUP_OUTCOME_PAID)
    return COLLECTION_STATE_PAID
  if (invoice.InvoiceFollowUpStatusCode === INVOICE_FOLLOWUP_STATUS_CONTACTED)
    return COLLECTION_STATE_CONTACTED
  if (invoice.InvoiceFollowUpStatusCode === INVOICE_FOLLOWUP_STATUS_ISSUE)
    return COLLECTION_STATE_ISSUE
  return COLLECTION_STATE_NOT_STARTED
}

export type CollectionFollowUpStateDisplay = {
  variant: BadgeProps["variant"]
  /** What the state means — the hover explanation. */
  description: string
  /** What an operator does about it; empty when nothing needs doing. */
  action?: string
}

// One entry per conversation state: color AND meaning, so the badge explains
// itself on hover — the same treatment the Fenerum-status and payment-health
// badges get.
export const COLLECTION_FOLLOW_UP_STATE_DISPLAY: Record<
  CollectionFollowUpState,
  CollectionFollowUpStateDisplay
> = {
  [COLLECTION_STATE_NOT_STARTED]: {
    variant: "critical",
    description:
      "Nobody has chased this invoice yet — money outstanding with no conversation behind it.",
    action:
      "Claim it (Owner column), call the customer, then record it via the row menu's Update Follow-up.",
  },
  [COLLECTION_STATE_CONTACTED]: {
    variant: "warning",
    description:
      "The customer has been reached — the Note and Promised columns carry the story.",
    action:
      "Chase again when the promise breaks; the Promised date turns red on its own.",
  },
  [COLLECTION_STATE_ISSUE]: {
    variant: "neutral",
    description:
      "Something is wrong with the invoice itself (disputed, mis-billed) and Finance has been told.",
    action:
      "Finance acts in Fenerum (credit, correction, write-off) — the board heals within the hour.",
  },
  [COLLECTION_STATE_PAID]: {
    variant: "positive-strong",
    description:
      "The mirror saw the money land in Fenerum — derived, never hand-marked. Kept on the board as the campaign's progress record.",
  },
}

// Days past due for an open row (settled rows read 0) — derived at render so
// it can never go stale.
export function getCollectionOverdueDays(
  invoice: Pick<CollectionInvoice, "DueDate" | "OutcomeCode">,
): number {
  if (!invoice.DueDate || invoice.OutcomeCode != null) return 0
  return Math.max(0, -getDaysUntil(String(invoice.DueDate)))
}

// The Overdue badge's escalation steps, shared by the board and the customer
// panel card: amber after two weeks, red after two months.
export function getCollectionOverdueVariant(
  overdueDays: number,
): BadgeProps["variant"] {
  if (overdueDays > 60) return "critical"
  if (overdueDays > 14) return "warning"
  return "neutral"
}

/**
 * Draft generators for the two emails the collections process still sends by
 * hand (C5: the call and the send stay human — we draft, a person reads,
 * edits and sends from their own mail client). Pure string builders: the
 * mailto: URL opens the owner's mailbox with everything prefilled, nothing
 * is ever sent by the app.
 */

function invoiceLine(invoice: CollectionInvoice) {
  const number = invoice.InvoiceNumber ?? invoice.InvoiceId
  const outstanding = formatInvoiceAmount(invoice.Balance, invoice.Currency)
  const due = invoice.DueDate
    ? formatDateString(String(invoice.DueDate))
    : "ukjent forfall"
  return `- Faktura #${number} — ${outstanding} utestående, forfalt ${due}\n  ${FENERUM_INVOICE_BASE_URL}/${invoice.ExternalInvoiceUuid}/`
}

function mailtoUrl(to: string, subject: string, body: string) {
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/**
 * The payment request to the debtor (FUS-1087): Norwegian, naming every open
 * invoice with the amount outstanding, the due date and its Fenerum link (we
 * hold no hosted payment link — the invoice page is where the PDF lives).
 * The recipient is left for the owner: we hold no billing contact email.
 */
export function buildPaymentRequestMailto(invoices: CollectionInvoice[]) {
  const debtorName =
    invoices[0]?.CustomerName ?? invoices[0]?.AccountCompanyName ?? ""

  const body = [
    "Hei,",
    "",
    `Vi viser til følgende utestående faktura${invoices.length === 1 ? "" : "er"} fra Placepoint:`,
    "",
    ...invoices.map(invoiceLine),
    "",
    "Vi ber om at beløpet betales snarest. Betalingsinformasjon fremgår av fakturaen.",
    "Har betalingen allerede funnet sted de siste dagene, kan du se bort fra denne henvendelsen — og si gjerne fra om noe er uklart.",
    "",
    "Med vennlig hilsen",
    "Placepoint",
  ].join("\n")

  return mailtoUrl(
    "",
    `Betalingspåminnelse fra Placepoint — ${debtorName}`,
    body,
  )
}

/**
 * The Finance handoff for a disputed invoice (FUS-1086): today's convention
 * kept — arbo@ with the customer name as the subject — but the body now
 * carries what Finance actually needs (org number, the invoice with its
 * outstanding amount and Fenerum link, and the dispute note), so nobody
 * composes it from memory. Resolution needs no reply path: Finance acts in
 * Fenerum and the mirror resolves the row within the hour.
 */
export function buildFinanceHandoffMailto(
  invoice: CollectionInvoice,
  note: string | null | undefined,
) {
  const debtorName =
    invoice.CustomerName ?? invoice.AccountCompanyName ?? "Ukjent kunde"

  const body = [
    "Hei,",
    "",
    "Det er meldt et problem med denne fakturaen (bestridt eller feil):",
    "",
    `Kunde: ${debtorName}${invoice.LegalVatNumber ? ` (org.nr ${invoice.LegalVatNumber})` : ""}`,
    invoiceLine(invoice),
    "",
    `Notat fra oppfølgingen:`,
    note?.trim() ? note.trim() : "(ingen notat — hør med eieren av saken)",
    "",
    "Rettelser gjøres i Fenerum; speilingen plukker opp resultatet innen en time.",
  ].join("\n")

  return mailtoUrl(FINANCE_COLLECTIONS_EMAIL, debtorName, body)
}
